import { useEffect, useRef } from 'react'

const DEFAULT_MIN_INTERVAL_MS = 10000

/**
 * Runs `onFocus` when the tab becomes visible/focused again (covers switching back to this tab,
 * restoring a minimized/backgrounded window, and OS-level app switching). This exists because
 * realtime connections (e.g. Stream's WebSocket) can go stale while a tab is backgrounded -
 * especially in mobile webviews, which may suspend JS entirely rather than firing a clean
 * disconnect - so returning to the tab is used as an explicit point to force a resync rather
 * than waiting on the connection's own reconnect/heartbeat timing.
 */
export const useSyncOnWindowFocus = (onFocus: () => void, minIntervalMs = DEFAULT_MIN_INTERVAL_MS): void => {
    const lastRunAtRef = useRef(0)
    const onFocusRef = useRef(onFocus)

    useEffect(() => {
        onFocusRef.current = onFocus
    }, [onFocus])

    useEffect(() => {
        const handleFocus = (): void => {
            if (document.visibilityState === 'hidden') {
                return
            }

            const now = Date.now()

            if (now - lastRunAtRef.current < minIntervalMs) {
                return
            }

            lastRunAtRef.current = now
            onFocusRef.current()
        }

        document.addEventListener('visibilitychange', handleFocus)
        window.addEventListener('focus', handleFocus)

        return () => {
            document.removeEventListener('visibilitychange', handleFocus)
            window.removeEventListener('focus', handleFocus)
        }
    }, [minIntervalMs])
}
